/**
 * Custom hook that takes the current wizard state and the initial snapshot, 
 * handle syncing back to the legacy listService, and trigger the actual save operation.
 */
import { useCallback } from 'react';
import listService from "ListService";
import mobileService from 'mobile/MobileService';
import mobileConstants from '../../../mobile/commons/MobileFormsConstants';

import {
    processControlModel,
    syncGlobalStylesToThemeService,
    calculateWizardThemeAttributes,
    ensurePxUnit,
    getMarginsFromAttributes,
    generateSectionProperties,
    generateAttributes
} from '../utils/mobileUtils';
import mobileListService from '../../../service/mobile/MobileListService';

export const useWizardSyncAndSave = ({
    layout,
    globalStyles,
    initialStateRef,
    setIsDirty
}) => {

    const save = useCallback(async (finishWizard = false) => {

        const { 
            initialObjectsSnapshot, 
            globalStyles: loadedGlobalStyles
        } = initialStateRef.current;

        try {
            syncReactStateToLegacyService(layout, globalStyles, initialObjectsSnapshot, loadedGlobalStyles, finishWizard);
            mobileListService.updateAllMobileControlsModels();
            const successMsg = await mobileService.saveApp();

            initialStateRef.current = {
                layout: JSON.parse(JSON.stringify(layout)),
                globalStyles: JSON.parse(JSON.stringify(globalStyles)),
                initialObjectsSnapshot: Object.values(listService.mobileObjects.toJS()).reduce((map, obj) => {
                    map[obj.id] = { id: obj.id, attributes: { ...(obj.attributes || {}) }, isNew: obj.isNew };
                    return map;
                }, {})
            };
            setIsDirty(false);

            return { success: true, message: successMsg };
        } catch (errorInfo) {
            if(errorInfo !== 0){
                const message = Array.isArray(errorInfo) ? errorInfo[0] : (errorInfo?.message || String(errorInfo) || 'An unknown error occurred.');
                console.error("Save operation failed:", errorInfo);
                return { success: false, message: message };
            }
            return { success: false, message: -1 };
        }
    }, [layout, globalStyles, initialStateRef, setIsDirty]);

    return { save };
};


const syncReactStateToLegacyService = (layout, globalStyles, initialObjectsSnapshot, loadedGlobalStyles, finishWizard = false) => {
    console.log("Syncing React State back to listService for mobile save...");

    //Check column structure
    const firstPageId = listService.pageList.first()?.id;
    if (firstPageId && !listService.getColumn(firstPageId, 0)) {
        console.warn("No columns found in listService. Creating new column structure for all pages...");
        
        listService.pageList.forEach(page => {
            const pageId = page.id;
            
            listService.createColumn(
                mobileConstants.CURRENT_COLUMN_NAME || 'Column', 
                mobileConstants.CURRENT_COLUMN_ORDER || 0, 
                pageId
            );
            
             listService.createColumn(
                mobileConstants.FIRST_COLUMN_NAME || 'Column 1',
                mobileConstants.FIRST_COLUMN_ORDER || 1, 
                pageId
            );
        });
    }

    listService.initMobileSections();
    
    listService.addMobileSetting('marginSpacing', globalStyles.marginSpacing);
    if (listService.currentForm?.model) { listService.currentForm.model.set('maxAppWidth', globalStyles.maxAppWidth, { silent: true }); }
    listService.addMobileSetting('fontFamily', globalStyles.fontFamily);

    globalStyles.fontStyling["control_margin"].marginSize = globalStyles.marginSpacing;
    globalStyles.fontStyling["control_margin"].defaultMargin = globalStyles.defaultMargin;

    listService.iniMobileStyle(globalStyles.fontStyling);

    syncGlobalStylesToThemeService(globalStyles);

    const allReactItemIds = new Set();

    const saveControlRecursively = (control, pageId, sectionId, parentGroupId = null) => {
        allReactItemIds.add(control.id);

        const initialObjectData = initialObjectsSnapshot[control.id];
        const initialAttributes = initialObjectData?.attributes || {};
        const currentAttributes = control.attributes || {};
        const wizardThemeAttributes = calculateWizardThemeAttributes(control, globalStyles);
        
        const finalControlAttributesToSave = generateAttributes(
            control, 
            globalStyles, 
            loadedGlobalStyles, 
            true
        );

        // Normalize font
        const newFontString = `${finalControlAttributesToSave.fontFamily},${finalControlAttributesToSave.fontSize},${finalControlAttributesToSave.fontWeight},${finalControlAttributesToSave.fontStyle}`;
        finalControlAttributesToSave.font = newFontString;

        if(control.factoryKey === "control_file_attachment"){
            const buttonFontString = `${finalControlAttributesToSave.buttonFontFamily},${finalControlAttributesToSave.buttonFontSize},${finalControlAttributesToSave.buttonTextWeight},${finalControlAttributesToSave.buttonTextStyle}`;
            finalControlAttributesToSave.buttonFont = buttonFontString;
        }

        if (control.factoryKey === 'control_table') {
            // Special Case: Tables use header-specific attributes
            finalControlAttributesToSave.headerFontFamily = finalControlAttributesToSave.fontFamily;
            finalControlAttributesToSave.headerFontSize = finalControlAttributesToSave.fontSize;
            finalControlAttributesToSave.headerTextWeight = finalControlAttributesToSave.fontWeight;
            finalControlAttributesToSave.headerTextStyle = finalControlAttributesToSave.fontStyle;
            finalControlAttributesToSave.headerFont = newFontString;
        } 

        //ensure the tabIndex is set
        finalControlAttributesToSave.tabIndex = control.tabIndex;
       
        const mobileObjectPayload = {
            id: control.id, 
            name: control.name, 
            factoryKey: control.factoryKey,
            pageId: pageId, 
            sectionId: sectionId, 
            order: control.order || 0,
            isHidden: false, 
            isNew: false, 
            groupId: parentGroupId || control.groupId,
            attributes: finalControlAttributesToSave,
            margins: getMarginsFromAttributes(finalControlAttributesToSave),
            height: ensurePxUnit(finalControlAttributesToSave.height || control.height),
            width: ensurePxUnit(finalControlAttributesToSave.width || control.width),
            mandatories: control.mandatories, 
            state: control.state,
        };

        listService.addMobileObject(control.id, mobileObjectPayload);

        if (Array.isArray(control.children) && control.children.length > 0) {
            control.children.forEach((child, index) => {
                child.order = index + 1; 
                saveControlRecursively(child, pageId, sectionId, control.id);
            });
        }
    };
   
    // Sync Pages, Columns, Sections, and Placed Controls
    Object.keys(layout).forEach(pageId => {
        if (!listService.mobilePages.has(pageId)) {
            const pageData = listService.pageList.get(pageId);
            if (pageData) listService.addMobilePage(pageId, { id: pageId, name: pageData.model.get('name') });
            else return;
        }
        allReactItemIds.add(pageId);
        listService.deleteMobileObjectsByPage(pageId);

        let defaultColumn = listService.getColumn(pageId, 0);
        if (!defaultColumn) {
            console.warn(`No default column found for page ${pageId}. Creating one.`);
            listService.createColumn(mobileConstants.CURRENT_COLUMN_NAME, mobileConstants.CURRENT_COLUMN_ORDER, pageId);
            defaultColumn = listService.getColumn(pageId, 0);
        }

        const defaultColumnId = defaultColumn.id;

        const reactSections = layout[pageId] || [];

        reactSections.forEach((section, sectionIndex) => {
            allReactItemIds.add(section.id);

            // --- Sync Section ---
            const { controls, attributes, ...sectionBase } = section;
            sectionBase.index = sectionIndex + 1;
            sectionBase.columnId = defaultColumnId;
            
            const finalAttributesToSave = generateSectionProperties(
                section,
                null,
                globalStyles,
                loadedGlobalStyles
            );
            const sectionPayload = {
                ...sectionBase,
                attributes: finalAttributesToSave,
            };
            listService.addMobileSection(section.id, sectionPayload);

            // --- Sync Controls ---
            section.controls.forEach((control, controlIndex) => {
                control.order = controlIndex + 1;
                saveControlRecursively(control, pageId, section.id, null);
            });
        });
    });

    // Sync Unplaced Controls
    listService.objectList.forEach(desktopControl => {
        if (desktopControl.id !== 'aForm' && !allReactItemIds.has(desktopControl.id)) {
            const initialObjectData = initialObjectsSnapshot[desktopControl.id];
            const baseControlData = processControlModel(desktopControl.model);

            if(!initialObjectData){
                console.log("The object has been removed from desktop.")
                return;
            }

            const initialAttributes = initialObjectData.attributes || {};
            const finalAttributes = {
                ...baseControlData.attributes,
                ...initialAttributes,
                isNew: baseControlData.isNew || false
            };

            let objectPayload;
            if (initialObjectData) { // Existed before, now hidden
                objectPayload = {
                    ...initialObjectData, id: desktopControl.id,
                    attributes: finalAttributes,
                    margins: getMarginsFromAttributes(initialObjectData.attributes || {}),
                    isHidden: true, sectionId: undefined, pageId: desktopControl.pageId,
                    order: initialObjectData.order || -1, isNew: finishWizard ? false : baseControlData.isNew || false,
                    name: baseControlData.name, factoryKey: baseControlData.factoryKey, groupId: baseControlData.groupId, // Add missing base props
                };
            } else { // New desktop control
                const themeAttributes = calculateWizardThemeAttributes(baseControlData, globalStyles);
                objectPayload = {
                    id: baseControlData.id, name: baseControlData.name, factoryKey: baseControlData.factoryKey,
                    pageId: desktopControl.pageId, sectionId: undefined, order: -1,
                    isHidden: true, isNew: finishWizard ? false : true, groupId: baseControlData.groupId,
                    attributes: themeAttributes,
                    margins: getMarginsFromAttributes(themeAttributes),
                    height: ensurePxUnit(themeAttributes.height), width: ensurePxUnit(themeAttributes.width),
                };
            }
            listService.addMobileObject(desktopControl.id, objectPayload);
        }
    });

    console.log("Sync complete. listService populated for mobileViewService.");
};

